import { useState, useEffect, useCallback } from "react";
import { api, apiBase } from "../api";
import type {
  ChangeRequestFromMe,
  Identity,
} from "../types";
import Icon from "../components/Icon";

type MyRequestsProps = {
  identity: Identity;
  onShowToast: (msg: string) => void;
  onOpenAsset: (assetId: string) => void;
};

function relTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const d = Math.round(hr / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

const EYEBROW: React.CSSProperties = {
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  textTransform: "uppercase" as const,
  letterSpacing: "0.1em",
  color: "var(--fg-muted)",
  fontWeight: 500,
};

export default function MyRequests({
  identity,
  onShowToast,
  onOpenAsset,
}: MyRequestsProps) {
  const [crs, setCrs] = useState<ChangeRequestFromMe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!apiBase()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.myChangeRequests({ status: "all" });
      setCrs(res.change_requests);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function del(cr: ChangeRequestFromMe) {
    setBusyId(cr.id);
    try {
      await api.deleteChangeRequest(cr.id);
      setCrs((prev) => prev.filter((c) => c.id !== cr.id));
      onShowToast("Request removed.");
    } catch (e: unknown) {
      onShowToast(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
      setConfirmDeleteId(null);
    }
  }

  // identity prop accepted for future per-identity behavior (e.g. filtering by id)
  void identity;

  return (
    <div className="page page-enter">
      <div style={{ maxWidth: 960, margin: "0 auto" }}>
        <div className="section-head">
          <div>
            <span className="eyebrow">Your submitted requests.</span>
            <h2 className="t-h2" style={{ marginTop: 10, fontSize: 32 }}>
              My requests.
            </h2>
          </div>
        </div>

        {error && (
          <div
            role="alert"
            style={{
              padding: "12px 16px",
              background: "rgba(229,138,109,0.10)",
              border: "1px solid rgba(229,138,109,0.35)",
              borderRadius: "var(--r-md)",
              color: "#e58a6d",
              marginBottom: 20,
              fontFamily: "var(--font-mono)",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <Icon name="x" size={14} />
            {error}
          </div>
        )}

        {loading ? (
          <div>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="skeleton"
                style={{
                  height: 72,
                  marginBottom: 8,
                  borderRadius: "var(--r-md)",
                }}
              />
            ))}
          </div>
        ) : crs.length === 0 ? (
          <div className="empty card" style={{ padding: "80px 24px" }}>
            <div className="glyph">
              <Icon name="inbox" size={22} />
            </div>
            <h4 className="t-h4">No requests yet.</h4>
            <p style={{ marginTop: 8 }}>
              Open an asset and use the Submit change button to request an update.
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {crs.map((cr) => (
              <MyCrRow
                key={cr.id}
                cr={cr}
                busy={busyId === cr.id}
                confirmDelete={confirmDeleteId === cr.id}
                onAskDelete={() => setConfirmDeleteId(cr.id)}
                onCancelDelete={() => setConfirmDeleteId(null)}
                onDelete={() => void del(cr)}
                onOpenAsset={() => onOpenAsset(cr.asset_id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MyCrRow({
  cr,
  busy,
  confirmDelete,
  onAskDelete,
  onCancelDelete,
  onDelete,
  onOpenAsset,
}: {
  cr: ChangeRequestFromMe;
  busy: boolean;
  confirmDelete: boolean;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onDelete: () => void;
  onOpenAsset: () => void;
}) {
  const isPending = cr.status === "pending";

  return (
    <div
      className="card card-pad"
      style={{
        padding: "16px 20px",
        opacity: isPending ? 1 : 0.7,
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 20,
        alignItems: "center",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, color: "var(--fg)", marginBottom: 4 }}>
          {cr.op_type === "field_update" ? (
            <>
              <span style={{ color: "var(--fg-muted)" }}>
                {cr.field_label ?? cr.field_name}:
              </span>{" "}
              <strong>{cr.new_value ?? "—"}</strong>
            </>
          ) : (
            <>
              <span style={{ color: "var(--fg-muted)" }}>
                Image update for slot:
              </span>{" "}
              <strong>{cr.image_slot ?? "—"}</strong>
            </>
          )}
        </div>

        <div
          style={{
            fontSize: 12,
            color: "var(--fg-muted)",
            fontFamily: "var(--font-mono)",
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            alignItems: "baseline",
          }}
        >
          <button
            className="btn-link"
            onClick={onOpenAsset}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              color: "var(--accent)",
              fontFamily: "inherit",
              fontSize: "inherit",
              textDecoration: "none",
            }}
          >
            {cr.asset_name}
          </button>
          <span>· Submitted {relTime(cr.submitted_at)}</span>
          <span>
            ·{" "}
            <strong
              style={{
                color: isPending ? "var(--warm-bright)" : "var(--accent)",
              }}
            >
              {isPending ? "Pending" : `Done ${relTime(cr.done_at)}`}
            </strong>
          </span>
        </div>

        {cr.note && (
          <div
            style={{
              marginTop: 8,
              padding: "8px 12px",
              borderLeft: "2px solid var(--rule-strong)",
              background: "var(--surface-sunk)",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--fg-muted)",
              borderRadius: 2,
            }}
          >
            “{cr.note}”
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        {isPending &&
          (confirmDelete ? (
            <>
              <span style={{ ...EYEBROW, color: "#e58a6d" }}>Remove?</span>
              <button
                className="btn btn-quiet btn-sm"
                onClick={onDelete}
                disabled={busy}
                style={{ color: "#e58a6d", fontSize: 12 }}
              >
                {busy ? "…" : "Yes, remove"}
              </button>
              <button
                className="btn btn-quiet btn-sm"
                onClick={onCancelDelete}
                style={{ fontSize: 12 }}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              className="btn btn-quiet btn-sm"
              onClick={onAskDelete}
              disabled={busy}
              style={{ color: "var(--fg-muted)", fontSize: 12 }}
            >
              <Icon name="trash" size={12} /> Delete
            </button>
          ))}
      </div>
    </div>
  );
}
